import { Col, Container, Row } from 'react-bootstrap';
import AppLoader from 'components/general/AppLoader';
import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchWrapper } from '_helpers/fetch-wrapper';
import { usePageNavigation } from 'components/navigation/NavigationContext';

import Menu from '../components/general/Menu';
import Footer from '../components/general/Footer';

import 'animate.css';
import { NextMeeting } from 'components/general/NextMeeting';
import PageBuilderViewer from 'plugins/pagebuilder/PageBuilderViewer';
import ConfigToolbar from 'components/FrontPage/ConfigToolbar';
import { ensureLegacyInstanceRegistered, resolveAgendaLinks } from 'services/instanceRegistration';
// import TmAi from 'components/FrontPage/TmAi';
import FullScreenModal from 'components/general/defaultModals/FullScreenModal';
import StandardModal from 'components/general/defaultModals/StandardModal';

function Main() {
  const DEFAULT_LOGO_INVITE_TEXT = 'Tap the logo \u2191 to open';
  const url = process.env.REACT_APP_URL;
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [openingNextMeeting, setOpeningNextMeeting] = useState(false);
  usePageNavigation({
    preferredOrientation: 'portrait',
    primaryAction: {
      label: 'Next Meeting', icon: 'bi-calendar-event', disabled: openingNextMeeting,
      onClick: async () => {
        setOpeningNextMeeting(true);
        try {
          const meeting = await fetchWrapper.get(`${url}/meetings/comingup`);
          if (meeting?._id) navigate(`/meeting/${meeting._id}`);
          else toast.info('No upcoming meeting is available.');
        } catch (error) {
          toast.error(`Unable to open the next meeting: ${error.message || error}`);
        } finally {
          setOpeningNextMeeting(false);
        }
      },
    },
  });

  const [homePageData, setHomePageData] = useState(null);
  const [clubData, setClubData] = useState(null);
  const [hintSequence, setHintSequence] = useState([]);
  const [currentHintIndex, setCurrentHintIndex] = useState(0);
  const [displayedHintText, setDisplayedHintText] = useState('');
  const [isDeletingHint, setIsDeletingHint] = useState(false);
  const [displayedInviteText, setDisplayedInviteText] = useState('');
  const [isDeletingInvite, setIsDeletingInvite] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showStandardModal, setShowStandardModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', body: '' });
  const [pageBuilderPageId, setPageBuilderPageId] = useState(() => searchParams.get('pageBuilderPage'));
  const [legacyRegistrationBootstrap, setLegacyRegistrationBootstrap] = useState(null);

  const fetchData = useCallback(async (endpoint, setData) => {
    try {
      const response = await fetch(`${url}/${endpoint}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (response.ok) {
        const data = await response.json();
        setData(Array.isArray(data) ? data[0] : data);
      } else {
        console.error(`Error fetching ${endpoint}:`, response.statusText);
      }
    } catch (error) {
      console.error(`Error fetching ${endpoint}:`, error);
    }
  }, [url]);

  const getHomePageData = useCallback(() => fetchData("front", setHomePageData), [fetchData]);
  const getClubData = useCallback(() => fetchData("club", setClubData), [fetchData]);

  const shuffleHints = useCallback((items) => {
    const list = [...items];
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }, []);

  const getHints = useCallback(async () => {
    try {
      const response = await fetch(`${url}/hints`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        console.error('Error fetching hints:', response.statusText);
        return;
      }
      const data = await response.json();
      const activeHints = Array.isArray(data)
        ? data.filter(h => h?.active && h?.content && h?.showOnMain !== false)
        : [];

      setHintSequence(shuffleHints(activeHints));
      setCurrentHintIndex(0);
    } catch (error) {
      console.error('Error fetching hints:', error);
    }
  }, [url, shuffleHints]);

  const updatePageBuilderLink = useCallback((pageId) => {
    const nextParams = new URLSearchParams(searchParams);
    if (pageId) nextParams.set('pageBuilderPage', pageId);
    else nextParams.delete('pageBuilderPage');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleShowHomeModal = (initialPageId = null) => {
    setPageBuilderPageId(initialPageId);
    setModalContent({
      title: clubData?.name || 'Home',
      ModalFooterComponent: <Footer />,
    });
    setShowModal(true);
  }

  const handleCloseHomeModal = () => {
    setShowModal(false);
    setPageBuilderPageId(null);
    updatePageBuilderLink(null);
  };

  const handlePageBuilderPageChange = useCallback((pageId) => {
    setPageBuilderPageId(pageId);
    updatePageBuilderLink(pageId);
  }, [updatePageBuilderLink]);

  const logoInviteText = (homePageData?.banner?.logoInviteText?.text || '').trim() || DEFAULT_LOGO_INVITE_TEXT;
  const logoInviteTextVisible = homePageData?.banner?.logoInviteText?.visible ?? true;
  const fallbackBannerSlogan = homePageData?.banner?.slogan0?.visible ? (homePageData?.banner?.slogan0?.text || '') : '';
  const clubSlogan = (clubData?.slogan || '').trim();
  const heroSlogan = clubSlogan || fallbackBannerSlogan;

  const handleLogoClick = () => {
    handleShowHomeModal();
  };

  // const handleShowAiModal = () => {
  //   setModalContent({
  //     title: 'AI Assistant',
  //     ModalBodyComponent: <TmAi />
  //   });
  //   setShowStandardModal(true);
  // }

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      await Promise.all([getHomePageData(), getClubData(), getHints()]);
      setIsLoading(false);
    };
    fetchAllData();
  }, [getHomePageData, getClubData, getHints]);

  useEffect(() => {
    let cancelled = false;

    const ensureRegistration = async () => {
      const links = resolveAgendaLinks();
      console.info('[LegacyRegistration] Home bootstrap started', {
        frontendRoot: links.frontendRoot,
        backendRoot: links.backendRoot
      });

      try {
        const result = await ensureLegacyInstanceRegistered();
        if (!cancelled) {
          setLegacyRegistrationBootstrap(result);
          console.info('[LegacyRegistration] Home bootstrap completed', result);
        }
      } catch (error) {
        if (!cancelled) {
          setLegacyRegistrationBootstrap({
            registered: false,
            skipped: false,
            unavailable: false,
            source: 'home-bootstrap-error',
            message: error?.message || 'Legacy instance registration bootstrap failed'
          });
          console.error('Legacy instance registration bootstrap failed:', error);
        }
      }
    };

    ensureRegistration();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const targetedPageId = searchParams.get('pageBuilderPage');
    if (!targetedPageId) return;
    setPageBuilderPageId(targetedPageId);
    setShowModal(true);
  }, [searchParams]);

  useEffect(() => {
    const handleHintsUpdated = () => {
      getHints();
    };

    window.addEventListener('tm-hints-updated', handleHintsUpdated);
    return () => window.removeEventListener('tm-hints-updated', handleHintsUpdated);
  }, [getHints]);

  useEffect(() => {
    setDisplayedHintText('');
    setIsDeletingHint(false);
  }, [currentHintIndex, hintSequence]);

  const currentHint = hintSequence[currentHintIndex] || hintSequence[0] || null;

  const formatCategoryLabel = (category) => category
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  useEffect(() => {
    if (!currentHint) {
      setDisplayedHintText('No active hints available');
      return undefined;
    }

    const fullText = currentHint.content || '';

    if (!isDeletingHint && displayedHintText.length < fullText.length) {
      const timeoutId = setTimeout(() => {
        setDisplayedHintText(fullText.slice(0, displayedHintText.length + 1));
      }, 60);
      return () => clearTimeout(timeoutId);
    }

    if (!isDeletingHint && displayedHintText.length === fullText.length) {
      const timeoutId = setTimeout(() => {
        setIsDeletingHint(true);
      }, 3000);
      return () => clearTimeout(timeoutId);
    }

    if (isDeletingHint && displayedHintText.length > 0) {
      const timeoutId = setTimeout(() => {
        setDisplayedHintText(fullText.slice(0, displayedHintText.length - 1));
      }, 16);
      return () => clearTimeout(timeoutId);
    }

    if (isDeletingHint && displayedHintText.length === 0) {
      const timeoutId = setTimeout(() => {
        setIsDeletingHint(false);
        setCurrentHintIndex(index => {
          if (index + 1 < hintSequence.length) {
            return index + 1;
          }

          setHintSequence(prevSequence => shuffleHints(prevSequence));
          return 0;
        });
      }, 50);
      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [currentHint, displayedHintText, isDeletingHint, hintSequence.length, shuffleHints]);

  useEffect(() => {
    const inviteFullText = logoInviteText || DEFAULT_LOGO_INVITE_TEXT;

    if (!isDeletingInvite && displayedInviteText.length < inviteFullText.length) {
      const timeoutId = setTimeout(() => {
        setDisplayedInviteText(inviteFullText.slice(0, displayedInviteText.length + 1));
      }, 60);
      return () => clearTimeout(timeoutId);
    }

    if (!isDeletingInvite && displayedInviteText.length === inviteFullText.length) {
      const timeoutId = setTimeout(() => {
        setIsDeletingInvite(true);
      }, 3000);
      return () => clearTimeout(timeoutId);
    }

    if (isDeletingInvite && displayedInviteText.length > 0) {
      const timeoutId = setTimeout(() => {
        setDisplayedInviteText(inviteFullText.slice(0, displayedInviteText.length - 1));
      }, 16);
      return () => clearTimeout(timeoutId);
    }

    if (isDeletingInvite && displayedInviteText.length === 0) {
      const timeoutId = setTimeout(() => {
        setIsDeletingInvite(false);
      }, 50);
      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [displayedInviteText, isDeletingInvite, logoInviteText]);

  useEffect(() => {
    setDisplayedInviteText('');
    setIsDeletingInvite(false);
  }, [logoInviteText]);

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <AppLoader label="Loading..." />
      </div>
    );
  }

  // Determine if we have any visible sections
  const hasVisibleSections = homePageData && (
    homePageData.banner.visible ||
    homePageData.nextMeeting.visible ||
    homePageData.forYou.visible ||
    homePageData.info.visible ||
    homePageData.contact.visible
  );

  return (
    <section className='home'>
      <Menu clubData={clubData} getClub={getClubData} />
      {/* <div className="agenda-toolbar-fixed">
        <div className="agenda-toolbar-icons">

          <Button variant="secondary" onClick={handleShowAiModal}>
            <i className="bi bi-robot"></i>
          </Button>
        </div>
      </div> */}
      <Container className='main-centered-content'>
        <Row className='justify-content-center'>
          <Col xs={12} lg={10}>
            <div className="main-hints-banner">
              <div className="main-hints-typewriter">
                <span className="main-hints-prefix">
                  <i className="bi bi-lightbulb me-2" />{formatCategoryLabel(currentHint?.category || 'Hints')}
                </span>
                <span className="main-hints-text">
                  {displayedHintText}
                  <span className="main-hints-cursor">|</span>
                </span>
              </div>
            </div>
          </Col>
        </Row>
        <Row className='justify-content-center text-center main-logo'>
          <Col xs={12}>
            <div className="animate__animated animate__zoomIn">
              <h1>{clubData?.name}</h1>
              <img
                className='main-logo-img'
                src={'/TMLogo.png'}
                alt="Header Img"
                onClick={handleLogoClick}
                style={{ cursor: 'pointer' }}
              />
              {logoInviteTextVisible && (
                <p className="main-logo-invite mt-1 mb-1">
                  {displayedInviteText}
                  <span className="main-hints-cursor">|</span>
                </p>
              )}
              {heroSlogan && <h2 className="bold mb-4">{heroSlogan}</h2>}
            </div>
          </Col>
        </Row>
      </Container>

      {hasVisibleSections && <NextMeeting fixedBottom />}
      <FullScreenModal
        show={showModal}
        handleClose={handleCloseHomeModal}
        title={modalContent.title || clubData?.name || 'Home'}
        ModalBodyComponent={showModal ? (
          <PageBuilderViewer
            initialPageId={pageBuilderPageId}
            onPageChange={handlePageBuilderPageChange}
          />
        ) : null}
        ModalFooterComponent={modalContent.ModalFooterComponent || <Footer />}
      />
      <StandardModal style={{ maxWidth: '80%', marginTop: '10%' }}
        show={showStandardModal}
        handleClose={() => setShowStandardModal(false)}
        title={modalContent.title}
        ModalBodyComponent={modalContent.ModalBodyComponent}
      />
      <ConfigToolbar
        homePageData={homePageData}
        getHomePageData={getHomePageData}
        clubData={clubData}
        getClubData={getClubData}
        legacyRegistrationBootstrap={legacyRegistrationBootstrap}
      />
      <Footer />
    </section>
  );
}
export default Main;